import { createSlice } from "@reduxjs/toolkit"
import isEqual from "lodash.isequal"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

import { frozenStateReconciler } from "../../../../Utils/persistHelpers"
import { logout } from "../../../LoginPage/redux/auth.actions"
import { preferencesSuccess } from "../../../User/redux/user.actions"
import { STATUS_OPTIONS, statusTextMap } from "../utils/constants"
import { REQUESTS_VERIFI_INFORM_SLICE_NAME } from "./requestsVerifiInform.constants"
import {
  getRequestsVerifiInform,
  getRequestVerifiInform,
  updateRequestVerifiInformStatus
} from "./requestsVerifiInform.thunks"

/* ------------- Initial State ------------- */

const columnsAdmin = [
  { field_id: "created", label: "Request date", active: true },
  { field_id: "updated", label: "Update status date", active: true },
  { field_id: "ba_name", label: "Business account", active: true },
  { field_id: "user_name", label: "User name", active: true }
]

const initialState = {
  fetching: false,
  result: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },

  defaultColumnsAdmin: [...columnsAdmin],
  columnsAdmin: [...columnsAdmin],

  filtersLastUpd: null,
  filters: {
    request_date: null,
    status: [],
    business_account: [],
    user_name: []
  },

  visibleFiltersAdmin: { status: true, user_name: true },
  filterOptions: {
    status: STATUS_OPTIONS.map((value) => ({ value, name: statusTextMap[value] }))
  },

  requestVerifiInform: null,
  requestVerifiInformFetching: false
}

const slice = createSlice({
  name: REQUESTS_VERIFI_INFORM_SLICE_NAME,
  initialState,
  reducers: {
    requestsVerifiInformSetProp(state, action) {
      const { key, value } = action.payload
      if (["filters", "visibleFilters", "visibleFiltersAdmin"]?.includes(key)) {
        if (key === "filters") state.filtersLastUpd = new Date().getTime()
        state[key] = { ...state?.[key], ...value }
      } else {
        state[key] = value
      }
    },
    clearRequestsVerifiInformFilters(state) {
      state.filters = initialState.filters
    }
  },
  extraReducers: (builder) => {
    builder
      /* ---------------- list ---------------- */
      .addCase(getRequestsVerifiInform.pending, (state) => {
        state.fetching = true
      })
      .addCase(getRequestsVerifiInform.fulfilled, (state, action) => {
        const result = action.payload
        state.fetching = false
        if (result?.results) {
          state.result = result
        } else {
          state.result = {
            count: result?.length || 0,
            results: result
          }
        }
      })
      .addCase(getRequestsVerifiInform.rejected, (state) => {
        state.fetching = false
      })

      /* ---------------- single request ---------------- */
      .addCase(getRequestVerifiInform.pending, (state) => {
        state.requestVerifiInform = null
        state.requestVerifiInformFetching = true
      })
      .addCase(getRequestVerifiInform.fulfilled, (state, action) => {
        state.requestVerifiInformFetching = false
        state.requestVerifiInform = action.payload
      })
      .addCase(getRequestVerifiInform.rejected, (state) => {
        state.requestVerifiInformFetching = false
      })

      /* ---------------- update status ---------------- */
      .addCase(updateRequestVerifiInformStatus.fulfilled, (state, action) => {
        const { id, status } = action.payload
        state.result.results = state.result.results?.map((item) =>
          item?.id === id ? { ...item, status } : item
        )
      })

      /* ---------------- logout ---------------- */
      .addCase(logout, (state) => ({
        ...initialState,
        filters: state.filters,
        columnsAdmin: state.columnsAdmin,
        visibleFiltersAdmin: state.visibleFiltersAdmin
      }))

      /* ---------------- restore saved preferences ---------------- */
      .addCase(preferencesSuccess, (state, { payload }) => {
        const preference = payload?.["requests-verifi-inform"]?.data
        if (!preference || preference.version !== REQUESTS_VERIFI_INFORM_PREFERENCES_VERSION)
          return
        if (!preference.filters || !preference.columnsAdmin || !preference.visibleFiltersAdmin)
          return

        state.filters = preference.filters
        state.columnsAdmin = preference.columnsAdmin
        state.visibleFiltersAdmin = preference.visibleFiltersAdmin
      })
  }
})

export const requestsVerifiInformActions = slice.actions

// Bump when the shape of the saved `{ version, filters, columnsAdmin, visibleFiltersAdmin }`
// payload changes.
export const REQUESTS_VERIFI_INFORM_PREFERENCES_VERSION = 2

/* ------------- Selectors ------------- */

export const selectAreFiltersDefault = (state) =>
  isEqual(state.requestsVerifiInform.filters, initialState.filters)

const PERSISTED_REDUCER_VERSION = 3

export const persistedReducer = persistReducer(
  {
    key: "requestsVerifiInform",
    storage,
    whitelist: ["filters", "visibleFiltersAdmin"],
    stateReconciler: frozenStateReconciler,
    version: PERSISTED_REDUCER_VERSION,
    migrate: async (state) => {
      if (state?._persist?.version < PERSISTED_REDUCER_VERSION) {
        return {
          ...state,
          ...initialState
        }
      }
      return state
    }
  },
  slice.reducer
)

export const reducer = persistedReducer
